import React, { useMemo, useState } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import chevronPrimaryRoundedIcon from '@/assets/icons/chevron-rounded-primary-down.svg';
import { getACGeneralInfoMetrics } from '@/utils/accountingCenterGeneralInfo';

interface ACData {
  description?: string;
  acmev?: number;
  acmev_alert_threshold?: number;
  acmev_used?: number;
  achov?: number;
  acoh?: number;
  acrt?: number;
  acrt_alert_threshold?: number;
  bmev?: number;
  bmev_used?: number;
  bhov?: number;
  btoh?: number;
  btrt?: number;
}

interface ACGeneralInfoAccordionProps {
  acData: ACData;
  budget?: Record<string, any>;
  isLoading?: boolean;
}

const ACGeneralInfoAccordion: React.FC<ACGeneralInfoAccordionProps> = ({ acData, budget, isLoading = false }) => {
  const [isOpen, setIsOpen] = useState(true);

  const metrics = useMemo(() => getACGeneralInfoMetrics(acData, budget), [acData, budget]);

  const dataItems = [
    {
      label: 'Description',
      value: isLoading ? '...' : acData.description || '-'
    },
    {
      label: 'ACMEV',
      value: isLoading ? '...' : `€${metrics.acmevTotal.toLocaleString()} (${metrics.acmevPercent}% of BMEV)`
    },
    {
      label: 'ACMEV Alerting Threshold',
      value: isLoading ? '...' : `${acData.acmev_alert_threshold ?? 0}%`
    },
    {
      label: 'ACHOV',
      value: isLoading ? '...' : `€${metrics.achov.toLocaleString()} (${metrics.acohvPercent}% of BHOV)`
    },
    {
      label: 'ACOH',
      value: isLoading ? '...' : `${metrics.acoh.toLocaleString()} Hours (${metrics.acohPercent}% of BTOH)`
    },
    {
      label: 'ACRT',
      value: isLoading ? '...' : `€${(acData.acrt || 0).toLocaleString()} (${metrics.acrtPercent}% of BTRT)`
    },
    {
      label: 'ACRT Alerting Threshold',
      value: isLoading ? '...' : `${acData.acrt_alert_threshold ?? 0}%`
    }
  ];

  return (
    <Accordion
      defaultValue="item-1"
      type="single"
      collapsible
      className="mt-3"
      onValueChange={(value) => setIsOpen(value === 'item-1')}
    >
      <AccordionItem value="item-1" className="bg-lightPurple rounded-[16px] overflow-hidden">
        <AccordionTrigger
          icon={<img src={chevronPrimaryRoundedIcon} alt="chevron icon" />}
          className={`py-0 min-h-[52px] px-4 ${isOpen ? 'border-b border-lightPurple-200' : ''}`}
        >
          <div>
            <p className="text-primary font-bold text-base">General Information</p>
          </div>
        </AccordionTrigger>
        <AccordionContent className="p-4 space-y-[1.1rem]">
          {dataItems.map((item, index) => (
            <div key={index}>
              <span className="text-[11px] text-lightPurple-300 font-medium">{item.label}</span>
              <p className="text-sm font-medium leading-[17px]">{item.value}</p>
            </div>
          ))}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default ACGeneralInfoAccordion;
